import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, CardBody, Col, Container, Form, Row } from 'shards-react';
import { useTranslation } from 'react-i18next';
import { RootState, useDispatch, useSelector } from '../redux/store';
import { postContinueUse, postStopUse, resetMessage, validateSecret } from '../redux/slices/confirmContinuation';
import queryString from 'query-string';
import type { FC } from 'react';
import type { ConfirmContinuationParams, ConfirmContinuationProps } from '../types/confirmContinuation';

const ConfirmContinuation: FC<ConfirmContinuationProps> = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { hasError, serverMessage, isValid } = useSelector((state: RootState) => state.confirmContinuation);
  const [email, setEmail] = useState<string>('');
  const [secret, setSecret] = useState<string>('');
  const [isContinueButtonActive, setIsContinueButtonActive] = useState<boolean>(true);
  const [isStopButtonActive, setIsStopButtonActive] = useState<boolean>(true);
  // サーバレスポンスに応じたメッセージを翻訳
  const translatedMessage: string | undefined =
    serverMessage === 'MESSAGE_INVALID_SECRET'
      ? t('confirm.message.invalid')
      : serverMessage === 'MESSAGE_SELECT_CONTINUATION'
      ? t('confirm.message.continuation')
      : serverMessage === 'MESSAGE_SELECT_DISCONTINUANCE'
      ? t('confirm.message.discontinuation')
      : serverMessage;

  // SIDE EFFECT
  // ================================================================================

  /**
   * 確認用
   */
  //  useEffect(() => {
  //   console.log('props: ', props);
  // }, []);

  /**
   * URLからemailとsecretを取得
   */
  useEffect(() => {
    const queryParams: ConfirmContinuationParams = queryString.parse(props.location.search);
    // console.log(queryParams);
    // emailが複数あったら不正なものとして空にする
    const paramsEmail: string | string[] = queryParams.email;
    const parsedEmail = Array.isArray(paramsEmail) ? '' : paramsEmail;
    // secretが複数あったら不正なものとして空にする
    const paramsSecret: string | string[] = queryParams.secret;
    const parsedSecret = Array.isArray(paramsSecret) ? '' : paramsSecret;
    setEmail(parsedEmail);
    setSecret(parsedSecret);
  }, []);

  /**
   * emailとsecretが揃ったらsecretを検証する
   */
  useEffect(() => {
    if (email && secret) {
      dispatch(validateSecret(email, secret));
    }
  }, [email, secret]);

  // EVENT HANDLER
  // ================================================================================

  /**
   * メッセージを消す
   */
  const dismiss = (): void => {
    dispatch(resetMessage());
  };

  /**
   * 継続利用を送信
   */
  const handleContinueUse = (): void => {
    // 回答ボタンをdisableにする
    setIsContinueButtonActive(false);
    setIsStopButtonActive(false);
    // 回答を送信
    dispatch(resetMessage());
    dispatch(postContinueUse(email));
  };

  /**
   * 利用中止を送信
   */
  const handleStopUse = (): void => {
    // 回答ボタンをdisableにする
    setIsContinueButtonActive(false);
    setIsStopButtonActive(false);
    // 回答を送信
    dispatch(resetMessage());
    dispatch(postStopUse(email));
  };

  // VIEW
  // ================================================================================

  if (isValid === undefined) {
    return <></>;
  }
  if (isValid === false) {
    return (
      <>
        <Container fluid className="main-content-container h-100 px-4">
          <Row noGutters className="h-100">
            <Col className="mx-auto my-auto">
              <Card className="mx-auto" style={{ maxWidth: '640px' }}>
                <CardBody>
                  <img className="auth-form__logo d-table mx-auto mb-3" src={`${process.env.PUBLIC_URL}/images/logo_timss_long-3.png`} alt="Mill Sheet Search System" />
                  <Row form className="mx-4">
                    <Col>
                      <div className="mx-auto p-4">
                        <Alert theme="danger" open={translatedMessage !== undefined}>
                          {translatedMessage}
                        </Alert>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }
  return (
    <>
      <Container fluid className="main-content-container h-100 px-4">
        <Row noGutters className="h-100">
          <Col className="mx-auto my-auto">
            <Card className="mx-auto" style={{ maxWidth: '640px' }}>
              <CardBody>
                <img className="auth-form__logo d-table mx-auto mb-3" src={`${process.env.PUBLIC_URL}/images/logo_timss_long-3.png`} alt="Mill Sheet Search System" />

                <Form className="mx-auto">
                  <Row form className="mx-4">
                    <Col>
                      <div className="mx-auto p-4">
                        <h4 className="form-text text-center">{t('confirm.title')}</h4>
                        <p className="form-text">{t('confirm.description')}</p>
                        <Alert className="mb-4" theme="danger" dismissible={dismiss} open={hasError && translatedMessage !== undefined}>
                          {translatedMessage}
                        </Alert>
                        <Alert className="mb-4" theme="success" dismissible={dismiss} open={!hasError && translatedMessage !== undefined}>
                          {translatedMessage}
                        </Alert>
                      </div>
                    </Col>
                  </Row>
                  <Row className="mx-4 mb-3">
                    <Col mb="4">
                      <Button pill block theme="primary" size="lg" className="h-100" onClick={handleContinueUse} disabled={!isContinueButtonActive}>
                        {t('confirm.select.continuation')}
                      </Button>
                    </Col>
                    <Col mb="4">
                      <Button pill block theme="danger" size="lg" className="h-100" onClick={handleStopUse} disabled={!isStopButtonActive}>
                        {t('confirm.select.discontinuation')}
                      </Button>
                    </Col>
                  </Row>
                  <Row>
                    <Col className="d-flex justify-content-center mb-4">
                      <small className="form-text text-muted">{t('confirm.note')}</small>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ConfirmContinuation;
